/**
 * DataStateCard — shared "card" used by every data-fetching surface
 * to render its loading / empty / error states uniformly.
 *
 * Originally introduced for the 5 analytics sub-pages
 * (`PortfolioOverview`, `PerformanceAnalytics`, `RiskAnalysis`,
 * `PositionManagement`, `TradingTools`) which had byte-identical
 * inline markup for both states.
 *
 * V2.I.6 extension: added an `'error'` variant with optional retry
 * action so the same primitive covers the third async-state branch.
 *
 * Three layouts:
 *  - **Loading** : icon (faded) + paragraph. Use `variant="loading"`.
 *  - **Empty**   : icon (faded) + h3 + paragraph. Use `variant="empty"`
 *    + a `title` prop. Optional `action` (CTA button).
 *  - **Error**   : icon (red-tinted) + h3 + paragraph. Use
 *    `variant="error"` + `title` + `message` (technical detail) +
 *    optional `onRetry` callback (renders a "Réessayer" button).
 *
 * No state, no hooks — pure presentation. The `aria-live` attribute
 * is set to `polite` on loading and `assertive` on error so screen
 * readers announce the transition appropriately.
 */

import type { ReactNode } from 'react';

export type DataStateVariant = 'loading' | 'empty' | 'error';

type HeadingLevel = 'h2' | 'h3' | 'h4';

interface DataStateCardProps {
  /** Faded icon rendered above the text (e.g. `<Icons.Activity size={48} />`). */
  icon: ReactNode;
  /** Visible to screen readers + visually as the card heading. Required
   *  for the empty and error variants; ignored for loading. */
  title?: string;
  /** Paragraph under the heading (or icon, on loading). */
  message: string;
  /** Visual mode. */
  variant: DataStateVariant;
  /** Optional CTA rendered under the message (empty variant). */
  action?: ReactNode;
  /** Retry callback — only rendered for the error variant.
   *  When provided, a "Réessayer" button is shown under the message. */
  onRetry?: () => void;
  /** Custom retry button label. Defaults to "Réessayer". */
  retryLabel?: string;
  /** Semantic heading level for the title. Default `h3`.
   *  Set `h2` when the closest ancestor heading is `h1` (page title)
   *  to avoid heading-order violations. */
  headingLevel?: HeadingLevel;
}

export function DataStateCard({
  icon,
  title,
  message,
  variant,
  action,
  onRetry,
  retryLabel = 'Réessayer',
  headingLevel = 'h3',
}: DataStateCardProps) {
  const HeadingTag = headingLevel;
  const ariaLive: 'polite' | 'assertive' | undefined =
    variant === 'loading' ? 'polite' : variant === 'error' ? 'assertive' : undefined;
  const ariaRole = variant === 'error' ? 'alert' : undefined;

  // Tint the icon with the danger colour on error so the state is
  // visually distinguishable at a glance.
  const iconWrapStyle: React.CSSProperties = {
    margin: '0 auto 16px',
    display: 'inline-block',
    opacity: variant === 'error' ? 0.85 : 0.3,
    color: variant === 'error' ? 'var(--danger)' : undefined,
  };

  const showHeading = (variant === 'empty' || variant === 'error') && title;

  return (
    <div
      className="card"
      style={{ textAlign: 'center', padding: '60px 20px' }}
      aria-live={ariaLive}
      role={ariaRole}
    >
      <div style={iconWrapStyle} aria-hidden>
        {icon}
      </div>
      {showHeading && (
        <HeadingTag style={{ marginBottom: '8px' }}>{title}</HeadingTag>
      )}
      <p
        style={{
          color: 'var(--muted)',
          marginBottom: variant === 'loading' ? 0 : '20px',
        }}
      >
        {message}
      </p>
      {variant === 'empty' && action}
      {variant === 'error' && onRetry && (
        <button
          type="button"
          className="btn-primary"
          onClick={onRetry}
          style={{ minWidth: '120px' }}
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}
